import React, { useState, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import ProblemStatement from './ProblemStatement';

interface QuestionAndCodeCardProps {
  problemTitle: string;
  problemDescription: string;
  submittedCode: string;
  submittedLanguage: string;
  className?: string;
  portalContainer?: HTMLElement | null;
}

const QuestionAndCodeCard: React.FC<QuestionAndCodeCardProps> = ({
  problemTitle,
  problemDescription,
  submittedCode,
  submittedLanguage,
  className,
  portalContainer,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fontSize, setFontSize] = useState(9);

  // Dynamic font size based on screen size (matching CodeSnippet)
  useEffect(() => {
    const updateFontSize = () => {
      const width = window.innerWidth;
      if (width >= 1280) {
        setFontSize(14); // xl
      } else if (width >= 1024) {
        setFontSize(11); // lg
      } else {
        setFontSize(9); // md and below
      }
    };

    updateFontSize();
    window.addEventListener('resize', updateFontSize);
    return () => window.removeEventListener('resize', updateFontSize);
  }, []);

  const displayLanguage = submittedLanguage?.trim() || 'plaintext';

  return (
    <>
      <div
        className={`flex flex-col h-full w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className || ''}`}
      >
        {/* Question Section */}
        <div className="flex flex-col gap-2 md:gap-1.5 lg:gap-2 xl:gap-2 p-6 md:p-3 lg:p-4 xl:p-6 border-b border-gray-100">
          {/* Question Title */}
          <h3 className="font-dm-sans text-base md:text-[12px] lg:text-[14px] xl:text-[16px] font-normal text-black leading-6 md:leading-4 lg:leading-5 xl:leading-6 tracking-[0.08px]">
            Question
          </h3>
          
          {/* Truncated Problem Text */}
          <p className="font-dm-sans text-base md:text-[10px] lg:text-[11px] xl:text-[16px] font-normal text-[#414447] leading-6 md:leading-4 lg:leading-5 xl:leading-6 tracking-[0.08px] line-clamp-3 overflow-hidden text-ellipsis">
            {problemTitle && (
              <span className="font-medium">{problemTitle}: </span>
            )}
            {problemDescription}
          </p>
          
          {/* Read More Button */}
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            className="self-start font-dm-sans text-base md:text-[10px] lg:text-[11px] xl:text-[15px] font-normal text-[#2563EB] hover:text-[#1d4ed8] leading-6 md:leading-4 lg:leading-5 xl:leading-6 tracking-[0.08px] underline underline-offset-2 transition-colors"
          >
            read more..
          </button>
        </div>

        {/* Your Answer Section */}
        <div className="flex flex-col flex-1 min-h-0 p-6 md:p-3 lg:p-4 xl:p-6 gap-3 md:gap-2 lg:gap-2.5 xl:gap-3">
          {/* Your Answer Label */}
          <h3 className="font-dm-sans text-lg md:text-[12px] lg:text-[14px] xl:text-[18px] font-medium text-black leading-[26px] md:leading-[18px] lg:leading-[20px] xl:leading-[26px] tracking-[0.27px]">
            Your Answer
          </h3>
          
          {/* Monaco Editor */}
          <div className="flex-1 min-h-0 rounded-lg border border-gray-200 overflow-hidden bg-[#FAFAFA]">
            <Editor
              value={submittedCode || '// No code submitted'}
              language={displayLanguage}
              theme="light"
              options={{
                readOnly: true,
                domReadOnly: true,
                padding: { top: 16, bottom: 16 },
                minimap: { enabled: false },
                lineNumbers: 'on',
                lineNumbersMinChars: 3,
                lineDecorationsWidth: 0,
                glyphMargin: false,
                folding: false,
                scrollBeyondLastLine: false,
                renderLineHighlight: 'none',
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                overviewRulerBorder: false,
                scrollbar: {
                  vertical: 'auto',
                  horizontal: 'auto',
                  verticalScrollbarSize: 8,
                  horizontalScrollbarSize: 8,
                },
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace",
                fontSize: fontSize,
                lineHeight: 1.6,
                selectionHighlight: false,
                occurrencesHighlight: 'off',
                cursorStyle: 'line-thin',
                cursorBlinking: 'solid',
                renderValidationDecorations: 'off',
                quickSuggestions: false,
                parameterHints: { enabled: false },
                suggestOnTriggerCharacters: false,
                acceptSuggestionOnEnter: 'off',
                tabCompletion: 'off',
                wordBasedSuggestions: 'off',
                selectionClipboard: false,
                contextmenu: false,
                mouseWheelZoom: false,
                links: false,
                hover: { enabled: false },
              }}
              height="100%"
            />
          </div>
        </div>
      </div>

      {/* Problem Statement Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className="sm:max-w-[70vw] max-w-[70vw]! w-[70vw] min-h-[60vh] max-h-[80vh] p-0 overflow-hidden rounded-2xl border-none shadow-2xl"
          portalContainer={portalContainer}
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">Problem Statement</DialogTitle>
          <div className="relative w-full h-full flex flex-col">
            {/* Custom Floating Close Button */}
            <button
              onClick={() => setIsDialogOpen(false)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/90 hover:bg-gray-100/50 backdrop-blur-sm shadow-sm border border-gray-100 transition-all text-gray-500 hover:text-gray-900"
            >
              <X size={18} />
            </button>

            <div className="flex-1 overflow-y-auto">
              <ProblemStatement
                instructionVisible={false}
                problemStatement={problemDescription}
                problemTitle={problemTitle}
                className="shadow-none border-none min-h-full pt-16"
                isPopup={true}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuestionAndCodeCard;

